/* ==========================================================================
   ZELICAT THEME — predictive-search.js
   Progressive enhancement of search:
   1. The header search icon (.site-header__search, a normal link to /search) opens a search dialog with live suggestions.
      Without JavaScript the link simply goes to the search page.
   2. The search page field ([data-predictive-search-form]) and the mobile menu search (form.mobile-search) show the same
      suggestions in a dropdown under the field.
   Suggestions are HTML rendered by sections/predictive-search.liquid (/search/suggest?section_id=predictive-search).
   CSS (assets/section-search.css) is injected lazily when it is not already on the page.

   Load it on every page with one line in layout/theme.liquid (deferred, after global.js). The data attributes are optional
   translated labels; if missing the script falls back to the header markup and English:
     <script src="{{ 'predictive-search.js' | asset_url }}" defer
       data-dialog-label="{{ 'predictive_search.dialog_label' | t }}"
       data-close-label="{{ 'predictive_search.close' | t }}"
       data-placeholder="{{ 'predictive_search.placeholder' | t: shop: shop.name }}"></script>
   ========================================================================== */
(() => {
  'use strict';
  if (window.__zcPredictiveSearch) return;
  window.__zcPredictiveSearch = true;

  const script = document.currentScript;
  const scriptSrc = script && script.src ? script.src : '';
  const dataset = (script && script.dataset) || {};

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const routes = () => (window.ZC && window.ZC.config && window.ZC.config.routes) || {};
  const searchUrl = () => routes().search || '/search';
  const suggestUrl = () => routes().predictiveSearch || '/search/suggest';
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const strings = {
    dialog: dataset.dialogLabel || '',
    close: dataset.closeLabel || 'Close',
    placeholder: dataset.placeholder || '',
  };

  /* ------------------------------------------------------------------ css, loaded once when needed */
  function ensureCss() {
    if ($('link[href*="section-search.css"]')) return;
    if (!scriptSrc) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = scriptSrc.replace(/predictive-search\.js/, 'section-search.css');
    document.head.appendChild(link);
  }

  /* ------------------------------------------------------------------ suggestion fetching, shared by dialog and inline fields */
  function bindSuggestions({ input, results, status, onStrings }) {
    const cache = new Map();
    let controller = null;
    let timer = null;
    let current = '';

    const setStatus = (text) => {
      if (!status) return;
      status.textContent = '';
      window.setTimeout(() => { status.textContent = text || ''; }, 50);
    };
    const clear = () => {
      current = '';
      results.innerHTML = '';
      results.hidden = true;
      setStatus('');
    };
    const show = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-predictive-results]', doc);
      if (!fresh) { clear(); return; }
      if (onStrings) onStrings(fresh.dataset);
      results.innerHTML = fresh.outerHTML;
      results.hidden = false;
      setStatus(fresh.dataset.status || '');
    };
    const run = async (term) => {
      if (term === current) return;
      current = term;
      if (!term) { clear(); return; }
      if (cache.has(term)) { show(cache.get(term)); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      const params = new URLSearchParams();
      params.set('q', term);
      params.set('section_id', 'predictive-search');
      params.set('resources[type]', 'query,product,collection,article,page');
      params.set('resources[limit]', '4');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', 'last');
      try {
        const res = await fetch(suggestUrl() + '?' + params.toString(), { signal: controller.signal });
        if (!res.ok) throw new Error('bad response');
        const html = await res.text();
        cache.set(term, html);
        if (term === current) show(html);
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        clear(); // on any error the visitor simply presses Enter and gets the normal search page
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const term = input.value.trim();
      if (!term) { if (controller) controller.abort(); clear(); return; }
      timer = window.setTimeout(() => run(term), 250);
    });

    /* arrow keys move between the input and the suggestion links */
    const items = () => $$('[data-predictive-item]', results);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        const list = items();
        if (list.length) { e.preventDefault(); list[0].focus(); }
      }
    });
    results.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const list = items();
      const i = list.indexOf(document.activeElement);
      if (i === -1) return;
      e.preventDefault();
      if (e.key === 'ArrowDown') { (list[i + 1] || list[i]).focus(); }
      else if (i === 0) input.focus();
      else list[i - 1].focus();
    });

    return { clear, run: (term) => run(term.trim()) };
  }

  /* ------------------------------------------------------------------ dialog opened from the header search icon */
  let modal = null;
  let opener = null;
  let releaseTrap = null;
  let previousOverflow = '';

  function fallbackStrings(from) {
    if (!strings.dialog) strings.dialog = (from && from.getAttribute('aria-label')) || 'Search';
    if (!strings.placeholder) {
      const mobile = $('form.mobile-search input[name="q"]');
      strings.placeholder = (mobile && mobile.getAttribute('placeholder')) || strings.dialog;
    }
  }

  function buildModal() {
    const icon = (paths) => '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' + paths + '</svg>';
    modal = document.createElement('div');
    modal.className = 'search-modal';
    modal.setAttribute('data-search-modal', '');
    modal.hidden = true;
    modal.innerHTML =
      '<button class="search-modal__scrim" type="button" tabindex="-1" aria-label="' + esc(strings.close) + '" data-search-close></button>' +
      '<div class="search-modal__panel" role="dialog" aria-modal="true" aria-label="' + esc(strings.dialog) + '">' +
        '<div class="search-modal__inner">' +
          '<form class="search-form" role="search" action="' + esc(searchUrl()) + '" method="get">' +
            '<label class="visually-hidden" for="zc-search-modal-input">' + esc(strings.dialog) + '</label>' +
            '<input id="zc-search-modal-input" type="search" name="q" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="' + esc(strings.placeholder) + '">' +
            '<input type="hidden" name="options[prefix]" value="last">' +
            '<button class="search-form__submit" type="submit" aria-label="' + esc(strings.dialog) + '">' + icon('<circle cx="11" cy="11" r="6.5"/><path d="M16 16l4.5 4.5"/>') + '</button>' +
            '<button class="icon-btn search-modal__close" type="button" aria-label="' + esc(strings.close) + '" data-search-close>' + icon('<path d="M6 6l12 12M18 6L6 18"/>') + '</button>' +
          '</form>' +
          '<div class="search-modal__results" id="zc-search-modal-results" hidden></div>' +
          '<p class="visually-hidden" role="status" aria-live="polite" data-predictive-status></p>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);

    const input = $('input[type="search"]', modal);
    const results = $('#zc-search-modal-results', modal);
    const status = $('[data-predictive-status]', modal);
    input.setAttribute('aria-controls', 'zc-search-modal-results');

    bindSuggestions({
      input,
      results,
      status,
      onStrings: (d) => {
        /* translated labels delivered by the section: use them for the next time the dialog opens */
        if (d.dialogLabel) strings.dialog = d.dialogLabel;
        if (d.closeLabel) strings.close = d.closeLabel;
        if (d.placeholder) strings.placeholder = d.placeholder;
        const panel = $('.search-modal__panel', modal);
        if (panel && d.dialogLabel) panel.setAttribute('aria-label', d.dialogLabel);
        if (d.closeLabel) $$('[data-search-close]', modal).forEach((b) => b.setAttribute('aria-label', d.closeLabel));
        if (d.placeholder) input.setAttribute('placeholder', d.placeholder);
      },
    });

    $$('[data-search-close]', modal).forEach((b) => b.addEventListener('click', closeModal));
    modal.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); closeModal(); } });
    /* results are links: leaving the page must not leave the dialog open when the visitor comes back */
    window.addEventListener('pageshow', (e) => { if (e.persisted && modal && !modal.hidden) closeModal(); });
  }

  function trap(container) {
    if (window.ZC && typeof window.ZC.focusTrap === 'function') return window.ZC.focusTrap(container);
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const f = $$('a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled])', container).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  function openModal(from) {
    ensureCss();
    fallbackStrings(from);
    if (!modal) buildModal();
    opener = from;
    if (opener) opener.setAttribute('aria-expanded', 'true');
    modal.hidden = false;
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    releaseTrap = trap($('.search-modal__panel', modal));
    const input = $('input[type="search"]', modal);
    input.focus();
    input.select();
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.style.overflow = previousOverflow;
    if (releaseTrap) { releaseTrap(); releaseTrap = null; }
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      if (document.contains(opener)) opener.focus();
    }
  }

  document.addEventListener('click', (e) => {
    const link = e.target.closest('.site-header__search');
    if (!link) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    openModal(link);
  });

  /* ------------------------------------------------------------------ inline suggestions under a search field */
  function initInline(form) {
    if (form.hasAttribute('data-zc-predictive')) return;
    const input = $('input[name="q"]', form);
    if (!input) return;
    form.setAttribute('data-zc-predictive', '');
    ensureCss();

    const holder = form.parentElement;
    const results = document.createElement('div');
    results.className = 'predictive-inline';
    results.hidden = true;
    results.id = 'zc-predictive-' + Math.random().toString(36).slice(2, 8);
    form.insertAdjacentElement('afterend', results);
    const status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    results.insertAdjacentElement('afterend', status);
    input.setAttribute('aria-controls', results.id);
    input.setAttribute('autocomplete', 'off');

    const api = bindSuggestions({ input, results, status });
    input.addEventListener('focus', () => { if (input.value.trim().length > 0 && results.hidden) api.run(input.value); });
    holder.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !results.hidden) { e.stopPropagation(); results.hidden = true; input.focus(); }
    });
    holder.addEventListener('focusout', () => {
      window.setTimeout(() => { if (!holder.contains(document.activeElement)) results.hidden = true; }, 0);
    });
    form.addEventListener('submit', () => { results.hidden = true; });
  }

  function initAll(root) {
    $$('form.mobile-search, [data-predictive-search-form] form, form[data-predictive-search-form]', root).forEach(initInline);
    const link = $('.site-header__search', root);
    if (link) link.setAttribute('aria-haspopup', 'dialog');
  }

  if (window.ZC && typeof window.ZC.mount === 'function') {
    window.ZC.mount(initAll);
  } else {
    const run = () => initAll(document);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
    else run();
    document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  }
})();
